import { fxRatesQueryOptions } from "@/lib/fxRates";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { RoCitySlugRoute } from "@/pages/CitySlugRoute";
import { routeHead } from "@/lib/seo/route-head";
import { listingsV2QueryOptions } from "@/lib/queries/publicData";
import { getAllCities, getCityBySlug } from "@/data/cities";
import { cityCopyRO, isKnownLocality, splitCityListings, topPlaces } from "@/utils/cityArea";
import { localStockCopy, localStockRobots } from "@/utils/localStockSeo";

const PREFIX = "masini-de-vanzare-";

export const Route = createFileRoute("/$roCitySlug/")({
  // This single-segment dynamic route only serves `/masini-de-vanzare-*`.
  // Anything else must be a real 404, not a 200 with the not-found page.
  beforeLoad: ({ params }) => {
    if (!params.roCitySlug?.startsWith(PREFIX)) throw notFound();
  },
  // Prime the cache server-side so the city listings are in the SSR HTML.
  // Unknown localities (not in our city list and named by no listing) are a 404;
  // a real locality with no stock right now stays 200 + noindex.
  loader: async ({ context, params }) => {
    const slug = params.roCitySlug.slice(PREFIX.length).toLowerCase();
    const rows = await context.queryClient
      .ensureQueryData(listingsV2QueryOptions({ country_code: "RO", limit: 5000 }))
      .catch(() => undefined);
    if (!Array.isArray(rows)) return null;
    const knownSlugs = getAllCities("RO").map((c) => c.slug);
    if (!isKnownLocality(slug, knownSlugs, rows)) throw notFound();
    const cityName =
      getCityBySlug(slug, "RO")?.name ??
      (rows as Array<{ location_city?: string | null }>).find(
        (l) => l.location_city && l.location_city.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") === slug,
      )?.location_city ??
      slug.replace(/-/g, " ");
    await context.queryClient.ensureQueryData(fxRatesQueryOptions()).catch(() => undefined);
    const { city, area } = splitCityListings(rows, slug, cityName);
    const copy = cityCopyRO({
      cityName,
      county: getCityBySlug(slug, "RO")?.county ?? null,
      cityCount: city.length,
      areaCount: area.length,
      areaPlaces: topPlaces(area),
    });
    return { cityCount: city.length, areaCount: area.length, copy, stockCopy: localStockCopy(city, cityName), rows };
  },
  head: ({ params, loaderData }) => {
    const base = routeHead("/$roCitySlug/", params as Record<string, string>);
    if (!loaderData) return base;
    const title = `${loaderData.copy.title} | RO Auto Market`;
    const desc = loaderData.copy.description;
    const meta = base.meta.map((m) => {
      if ("title" in m && Object.keys(m).length === 1) return { title };
      const key = m.property || m.name;
      if (key === "title" || key === "og:title" || key === "twitter:title") return { ...m, content: title };
      if (key === "description" || key === "og:description" || key === "twitter:description") return { ...m, content: desc };
      return m;
    });
    return {
      meta: [...meta.filter((m) => m.name !== 'robots'), { name: 'robots', content: localStockRobots(loaderData.cityCount) }],
      links: base.links,
      scripts: [{ type: 'application/ld+json', children: JSON.stringify({ '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: loaderData.stockCopy.faqs.map((faq) => ({ '@type': 'Question', name: faq.question, acceptedAnswer: { '@type': 'Answer', text: faq.answer } })) }) }],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const data = Route.useLoaderData();
  return (
    <RoCitySlugRoute initialListings={data?.rows} />
  );
}
